import React, { useEffect, useState } from 'react';

import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';

import { Button } from '@qality/common-components/src/atoms/Button';
import { Text } from '@qality/common-components/src/atoms/Text';
import { useExportTestCasesContext } from '@qality/common-components/src/lib/ExportTestCasesProvider';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { SERVICEDESK_URL } from '@qality/common-config/src/globalVariables';

import {
  ExportTestCasesWrapperStyled,
  TextStyled,
} from './ExportTestCasesModal.styled';

type Props = {
  onCloseDialog?: () => void;
  autoExportFromUrl?: boolean;
};

export function ExportTestCasesModal({
  onCloseDialog,
  autoExportFromUrl = false,
}: Props): JSX.Element {
  const { numberOfExportedIssues, exportStatus, exportTestCases } =
    useExportTestCasesContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  let loadingText = '...';
  let isLoading = true;

  const handleOnClose = (): void => {
    setIsModalOpen(false);
    onCloseDialog?.();
  };

  if (exportStatus === 'init') {
    loadingText = 'Fetching Issues data';
  } else if (exportStatus === 'fetchSteps') {
    loadingText = `Fetching Steps for ${numberOfExportedIssues} Test Case${
      numberOfExportedIssues > 1 ? 's' : ''
    }`;
  } else if (exportStatus === 'makeFile') {
    loadingText = 'Making CSV file';
  } else if (exportStatus === 'error') {
    isLoading = false;
  } else if (exportStatus === 'success') {
    isLoading = false;
  } else if (exportStatus === 'noIssues') {
    isLoading = false;
  }

  useEffect(() => {
    if (exportStatus === 'init') {
      setIsModalOpen(true);
    }
    if (exportStatus === 'success') {
      handleOnClose();
    }
  }, [exportStatus]);

  useEffect(() => {
    if (autoExportFromUrl) {
      exportTestCases({ source: 'url' });
      setIsModalOpen(true);
    }
  }, []);

  return (
    <ModalTransition>
      {isModalOpen && (
        <Modal
          onClose={handleOnClose}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEscapePress={true}
          shouldScrollInViewport
        >
          <ModalHeader data-testid='ExportTestCases_Header'>
            <ModalTitle>Export Test Cases with Steps</ModalTitle>
          </ModalHeader>
          <ModalBody data-testid='ExportTestCases_Body'>
            <ExportTestCasesWrapperStyled>
              {isLoading ? (
                <SpinnerLoader size='large' text={loadingText} />
              ) : (
                <>
                  {exportStatus === 'error' && (
                    <TextStyled>
                      Sorry, we couldn&apos;t export the Test Cases. Please try
                      again later or contact our{' '}
                      <a
                        href={SERVICEDESK_URL}
                        rel='noreferrer'
                        target='_blank'
                      >
                        Support
                      </a>{' '}
                      if the problem persists
                    </TextStyled>
                  )}
                  {exportStatus === 'noIssues' && (
                    <Text>There are no Test Cases to export</Text>
                  )}
                </>
              )}
            </ExportTestCasesWrapperStyled>
          </ModalBody>
          <ModalFooter data-testid='ExportTestCases_Footer'>
            <Button onClick={handleOnClose}>
              {exportStatus === 'error' || exportStatus === 'noIssues'
                ? 'Close'
                : 'Cancel'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
}
